'use client'

import { resolveSelectedClaimContext } from '@/tiptap/editor/components/notion-like-editor-toolbar-floating'
import { Button as TiptapButton } from '@/tiptap/tiptap-ui-primitive/button'
import { Spacer } from '@/tiptap/tiptap-ui-primitive/spacer'
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/tiptap/tiptap-ui-primitive/toolbar'
import { ColorTextPopover } from '@/tiptap/tiptap-ui/color-text-popover'
import { HeadingDropdownMenu } from '@/tiptap/tiptap-ui/heading-dropdown-menu'
import { ImageUploadButton } from '@/tiptap/tiptap-ui/image-upload-button'
import { MarkButton } from '@/tiptap/tiptap-ui/mark-button'
import { TextAlignButton } from '@/tiptap/tiptap-ui/text-align-button'
import { UndoRedoButton } from '@/tiptap/tiptap-ui/undo-redo-button'
import { useEditorState, type Editor } from '@tiptap/react'
import {
  BoldIcon,
  Bot,
  Code2Icon,
  Heading1Icon,
  ItalicIcon,
  Lightbulb,
  MessageSquare,
  Redo2Icon,
  RemoveFormatting,
  StrikethroughIcon,
  UnderlineIcon,
  Undo2Icon,
} from 'lucide-react'

function DisabledToolbarButton({
  icon: Icon,
  label,
}: {
  icon: typeof Undo2Icon
  label: string
}) {
  return (
    <TiptapButton
      type='button'
      disabled
      data-style='ghost'
      aria-label={label}
      tooltip={label}
    >
      <Icon className='tiptap-button-icon' />
    </TiptapButton>
  )
}

export const MainToolbar = ({
  editor,
  enableComments = false,
  enableColorAndAlignment = false,
  commentActionLabel,
  onOpenComments,
  onAskVera,
  onAddClaimFromSelection,
}: {
  editor: Editor
  enableComments?: boolean
  enableColorAndAlignment?: boolean
  commentActionLabel?: string
  onOpenComments?: () => void
  onAskVera?: (payload: { quoteText?: string; docBlockId?: string }) => void
  onAddClaimFromSelection?: (payload: {
    docBlockId: string
    quoteText: string
    offset: number
  }) => void
}) => {
  const hasSelection =
    useEditorState({
      editor,
      selector: ({ editor }) =>
        Boolean(editor && !editor.state.selection.empty),
    }) ?? false

  if (!editor.isEditable) {
    return (
      <Toolbar>
        <ToolbarGroup>
          <DisabledToolbarButton icon={Undo2Icon} label='Undo' />
          <DisabledToolbarButton icon={Redo2Icon} label='Redo' />
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <DisabledToolbarButton icon={Heading1Icon} label='Heading' />
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <DisabledToolbarButton icon={BoldIcon} label='Bold' />
          <DisabledToolbarButton icon={ItalicIcon} label='Italic' />
          <DisabledToolbarButton icon={StrikethroughIcon} label='Strike' />
          <DisabledToolbarButton icon={Code2Icon} label='Inline code' />
          <DisabledToolbarButton icon={UnderlineIcon} label='Underline' />
        </ToolbarGroup>

        <Spacer />
      </Toolbar>
    )
  }

  return (
    <Toolbar>
      <ToolbarGroup>
        <UndoRedoButton editor={editor} action='undo' />
        <UndoRedoButton editor={editor} action='redo' />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu editor={editor} levels={[1, 2, 3, 4]} />
        {enableColorAndAlignment && (
          <>
            <ColorTextPopover
              editor={editor}
              colorType='text'
              popoverPortal
            />
            <ColorTextPopover
              editor={editor}
              colorType='highlight'
              popoverPortal
            />
          </>
        )}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton editor={editor} type='bold' />
        <MarkButton editor={editor} type='italic' />
        <MarkButton editor={editor} type='strike' />
        <MarkButton editor={editor} type='code' />
        <MarkButton editor={editor} type='underline' />
        <MarkButton editor={editor} type='kbd' />
        <TiptapButton
          type='button'
          onClick={() => editor.chain().focus().unsetAllMarks().run()}
          disabled={!editor.can().unsetAllMarks()}
          data-style='ghost'
          aria-label='Clear formatting'
          tooltip='Clear formatting'
          shortcutKeys='Mod-\\'
        >
          <RemoveFormatting className='tiptap-button-icon' />
        </TiptapButton>
      </ToolbarGroup>

      {enableColorAndAlignment && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            <TextAlignButton editor={editor} align='left' />
            <TextAlignButton editor={editor} align='center' />
            <TextAlignButton editor={editor} align='right' />
            <TextAlignButton editor={editor} align='justify' />
            <ImageUploadButton editor={editor} />
          </ToolbarGroup>
        </>
      )}

      {(onAskVera || onAddClaimFromSelection) && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            {onAskVera && (
              <TiptapButton
                type='button'
                data-style='ghost'
                aria-label='Ask Vera'
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  const selection = resolveSelectedClaimContext(editor)
                  onAskVera(
                    selection
                      ? {
                          quoteText: selection.quoteText,
                          docBlockId: selection.docBlockId,
                        }
                      : {}
                  )
                }}
                tooltip='Ask Vera AI for help'
              >
                <Bot className='tiptap-button-icon text-cobalt-strong' />
              </TiptapButton>
            )}
            {onAddClaimFromSelection && (
              <TiptapButton
                type='button'
                data-style='ghost'
                aria-label='Add claim'
                disabled={!hasSelection}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  const selection = resolveSelectedClaimContext(editor)
                  if (selection) onAddClaimFromSelection(selection)
                }}
                tooltip='Add claim'
              >
                <Lightbulb className='tiptap-button-icon' />
              </TiptapButton>
            )}
          </ToolbarGroup>
        </>
      )}

      {enableComments && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            <TiptapButton
              type='button'
              data-style='ghost'
              aria-label={commentActionLabel ?? 'Add comment'}
              tooltip={commentActionLabel ?? (hasSelection ? 'Comment on selected text' : onOpenComments ? 'Open comments' : 'Select text to add a comment')}
              disabled={!hasSelection && !onOpenComments}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (editor.state.selection.empty) {
                  onOpenComments?.()
                  return
                }
                editor.chain().focus().setComment('').run()
              }}
            >
              <MessageSquare className='tiptap-button-icon' />
            </TiptapButton>
          </ToolbarGroup>
        </>
      )}

      <Spacer />
    </Toolbar>
  )
}
